/**
 * SMS segment math for the dashboard.
 *
 * This is a faithful mirror of the server's canonical implementation in
 * `server/src/db/controllers/message.controller/sms-segment-helpers.ts` plus
 * the GSM-7 sanitization map in `server/src/utils/twilio/gsm7-sanitize.ts`.
 * Keep these in sync — the backend is the source of truth for what actually
 * gets billed and sent.
 *
 * Twilio bills, and carriers deliver, SMS in "segments". A message that fits in
 * a single segment holds 160 GSM-7 characters (or only 70 UCS-2 characters once
 * any non-GSM-7 character — most commonly an emoji or smart-quote — forces the
 * whole message into UCS-2). Longer messages are concatenated from segments of
 * 153 (GSM-7) / 67 (UCS-2) characters.
 *
 * Carriers begin rejecting long concatenated messages with Twilio error 30019
 * well before Twilio's own 1,600-character hard reject (error 21617). To stay
 * safely under that carrier ceiling we cap segment counts per authoring surface
 * (see {@link SMS_SEGMENT_LIMITS}) in the UI and enforce an absolute ceiling
 * ({@link SMS_SEGMENT_HARD_CEILING}) on the backend.
 */

export type SmsEncoding = 'GSM-7' | 'UCS-2'

export interface SmsSegmentInfo {
    encoding: SmsEncoding
    characterCount: number
    segments: number
}

/**
 * A non-GSM-7 character found in authored SMS text, grouped by code point.
 * Used to explain why a message flipped to UCS-2 and to power the one-click
 * "Fix special characters" action.
 */
export interface NonGsm7Character {
    char: string
    /** e.g. "U+00A0" */
    codePoint: string
    /** Human-readable name, e.g. "non-breaking space" */
    name: string
    count: number
    /** True when {@link sanitizeForGsm7} will replace or remove this character. */
    isAutoFixable: boolean
    /** Short snippet of surrounding text for the first occurrence. */
    exampleContext: string
}

/**
 * Per-surface maximum SMS segments enforced in the dashboard UI. These sit
 * below {@link SMS_SEGMENT_HARD_CEILING} on purpose: authors get tight,
 * use-case-appropriate guardrails while the backend backstops everything —
 * including personalization that only expands server-side — at the hard ceiling.
 */
export const SMS_SEGMENT_LIMITS = {
    /** Bulk mass-communication marketing sends. */
    bulkMessage: 8,
    /** Automated notification templates. */
    notification: 7,
    /** Auto-reply to inbound client messages. */
    autoReply: 4,
    /** Manually typed messages sent from the inbox / chat composer. */
    inboxMessage: 14,
} as const

export type SmsSegmentSurface = keyof typeof SMS_SEGMENT_LIMITS

/**
 * Absolute maximum segments allowed for ANY single SMS, enforced on the backend
 * in `handleSendToClientViaSms`. Surfaced here so the UI can explain the ceiling
 * consistently. Messages above this are extremely likely to be rejected by the
 * destination carrier (Twilio error 30019).
 */
export const SMS_SEGMENT_HARD_CEILING = 20

/**
 * Length of every SMS tracking short link after send, e.g.
 * `https://chck.vet/5GMZzlCG2DM7PKkzuRPhY/0`. Keep in sync with
 * `SMS_SHORTENED_LINK_PLACEHOLDER` in the server's `sms-segment-helpers.ts`.
 */
export const SMS_SHORTENED_LINK_PLACEHOLDER =
    'https://chck.vet/5GMZzlCG2DM7PKkzuRPhY/0'

/**
 * Keep in sync with `BULK_MARKETING_SMS_OPT_OUT_*` in the server's
 * `sms-segment-helpers.ts`. Marketing bulk SMS append this footer at send.
 */
export const BULK_MARKETING_SMS_OPT_OUT_KEYWORD = 'NOCOMMS'
export const BULK_MARKETING_SMS_OPT_OUT_POSTSCRIPT = `Reply "${BULK_MARKETING_SMS_OPT_OUT_KEYWORD}" to unsubscribe from marketing texts.`

/**
 * Same matcher the server's `replaceWithTrackableUrls` uses so the bulk
 * composer meter agrees with send on what counts as a rewriteable link.
 */
const SMS_TRACKABLE_URL_PATTERN =
    /\b((?:https?:\/\/)(?:[-a-zA-Z0-9@:%._+~#=]+(?:\.[-a-zA-Z0-9@:%._+~#=]+)*)(?:\/[^\s]*)?)\b([.,!?])?/g

export function replaceUrlsWithSmsShortLinkLength(text: string): string {
    return text.replace(
        SMS_TRACKABLE_URL_PATTERN,
        (_full, _url, trailingPunc: string | undefined) =>
            `${SMS_SHORTENED_LINK_PLACEHOLDER}${trailingPunc || ''}`,
    )
}

/**
 * When a clinic has `tmp_feature_flags.ignore_sms_character_limit`, dashboard
 * soft segment caps (the length meter and save/send gates) are skipped. The
 * backend hard ceiling ({@link SMS_SEGMENT_HARD_CEILING}) still applies.
 */
export function shouldIgnoreSmsCharacterLimit(
    tmpFeatureFlags:
        | { ignore_sms_character_limit?: boolean }
        | null
        | undefined,
): boolean {
    return tmpFeatureFlags?.ignore_sms_character_limit === true
}

/**
 * When a clinic has `tmp_feature_flags.skip_trackable_urls`, send leaves
 * authored URLs in the SMS body instead of rewriting them to tracking short
 * links. Estimates should then count the true URL length.
 */
export function shouldSkipTrackableUrls(
    tmpFeatureFlags: { skip_trackable_urls?: boolean } | null | undefined,
): boolean {
    return tmpFeatureFlags?.skip_trackable_urls === true
}

// GSM-7 default alphabet. Characters here encode in a single 7-bit unit.
const GSM_7_BASIC_CHARACTERS = new Set(
    Array.from(
        '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\u001bÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà',
    ),
)

// GSM-7 extension table. Each of these encodes as two 7-bit units.
const GSM_7_EXTENDED_CHARACTERS = new Set(Array.from('^{}\\[~]|€'))

/**
 * Mirror of `server/src/utils/twilio/gsm7-sanitize.ts`. Common paste/autocorrect
 * offenders → GSM-7-safe ASCII. Deliberately does not rewrite emoji.
 */
const GSM7_REPLACEMENTS: Record<string, string> = {
    '\u2018': "'",
    '\u2019': "'",
    '\u201A': "'",
    '\u201B': "'",
    '\u2032': "'",
    '\u201C': '"',
    '\u201D': '"',
    '\u201E': '"',
    '\u201F': '"',
    '\u2033': '"',
    '\u2013': '-',
    '\u2014': '-',
    '\u2212': '-',
    '\u2026': '...',
    '\u00A0': ' ',
    '\u2009': ' ',
    '\u200A': ' ',
    '\u202F': ' ',
    '\u2022': '*',
    '\u2023': '*',
    '\u200B': '',
    '\u200C': '',
    '\u200D': '',
    '\uFEFF': '',
}

const GSM7_REPLACEMENT_REGEX = new RegExp(
    `[${Object.keys(GSM7_REPLACEMENTS).join('')}]`,
    'g',
)

const NON_GSM7_CHARACTER_NAMES: Record<string, string> = {
    '\u00A0': 'non-breaking space',
    '\u2009': 'thin space',
    '\u200A': 'hair space',
    '\u202F': 'narrow non-breaking space',
    '\u200B': 'zero-width space',
    '\u200C': 'zero-width non-joiner',
    '\u200D': 'zero-width joiner',
    '\uFEFF': 'byte-order mark',
    '\u2018': 'smart single quote',
    '\u2019': 'smart apostrophe',
    '\u201A': 'smart single quote',
    '\u201B': 'smart single quote',
    '\u2032': 'prime mark',
    '\u201C': 'smart double quote',
    '\u201D': 'smart double quote',
    '\u201E': 'smart double quote',
    '\u201F': 'smart double quote',
    '\u2033': 'double prime mark',
    '\u2013': 'en dash',
    '\u2014': 'em dash',
    '\u2212': 'minus sign',
    '\u2026': 'ellipsis character',
    '\u2022': 'bullet',
    '\u2023': 'triangle bullet',
}

/**
 * Replaces common non-GSM-7 paste/autocorrect characters with ASCII equivalents
 * so the SMS can stay in GSM-7. Matches the server send-path sanitizer.
 */
export function sanitizeForGsm7(text: string): string {
    if (!text) return text
    return text.replace(
        GSM7_REPLACEMENT_REGEX,
        ch => GSM7_REPLACEMENTS[ch] ?? ch,
    )
}

/**
 * Finds non-GSM-7 characters in `text`, grouped by code point, with enough
 * context for the UI to explain why a message flipped to UCS-2.
 */
export function findNonGsm7Characters(text: string): NonGsm7Character[] {
    if (!text) return []

    const chars = Array.from(text)
    const byChar = new Map<
        string,
        {
            count: number
            firstIndex: number
        }
    >()

    for (let i = 0; i < chars.length; i++) {
        const character = chars[i]
        if (isGsm7Character(character)) continue

        const existing = byChar.get(character)
        if (existing) {
            existing.count += 1
        } else {
            byChar.set(character, { count: 1, firstIndex: i })
        }
    }

    return Array.from(byChar.entries())
        .map(([char, { count, firstIndex }]) => {
            const code = char.codePointAt(0)?.toString(16).toUpperCase() ?? '?'
            return {
                char,
                codePoint: `U+${code.padStart(4, '0')}`,
                name: getNonGsm7CharacterName(char),
                count,
                isAutoFixable: Object.prototype.hasOwnProperty.call(
                    GSM7_REPLACEMENTS,
                    char,
                ),
                exampleContext: buildExampleContext(chars, firstIndex),
            }
        })
        .sort((a, b) => {
            if (a.isAutoFixable !== b.isAutoFixable) {
                return a.isAutoFixable ? -1 : 1
            }
            return b.count - a.count
        })
}

/**
 * Computes the encoding, character (unit) count, and segment count for a piece
 * of SMS text, matching Twilio's segmentation rules.
 */
export function getSmsSegmentInfo(text: string): SmsSegmentInfo {
    const encoding = getSmsEncoding(text)
    const characterCount =
        encoding === 'GSM-7' ? getGsm7UnitCount(text) : text.length

    if (characterCount === 0) {
        return { encoding, characterCount, segments: 0 }
    }

    const singleSegmentLimit = encoding === 'GSM-7' ? 160 : 70
    const concatenatedSegmentLimit = encoding === 'GSM-7' ? 153 : 67
    const segments =
        characterCount <= singleSegmentLimit
            ? 1
            : Math.ceil(characterCount / concatenatedSegmentLimit)

    return { encoding, characterCount, segments }
}

export interface BuildClinicSmsPreviewInput {
    body: string
    ctaText?: string | null
    ctaUrl?: string | null
    clinicName?: string | null
    excludeMessageWrapper?: boolean | null
    /**
     * When true, every `http(s)://` URL in the preview is counted at the
     * tracking short-link length instead of the authored URL. Use for bulk
     * SMS, which always rewrites links at send time.
     */
    assumeShortenedLinks?: boolean
    /**
     * When true, append the bulk marketing opt-out postscript so the
     * character meter matches what marketing mass-comms actually send.
     */
    includeMarketingOptOutFooter?: boolean | null
}

/**
 * Minimal, drift-safe mirror of the server's `buildClinicSmsMessageText`. It
 * reconstructs the parts of the rendered message that materially affect length —
 * the "Message from {clinic}:" wrapper (inbox/auto-reply only), the
 * call-to-action line, and the marketing bulk opt-out footer — so the UI
 * counts segments against something close to what the client will actually
 * receive. Pass {@link excludeMessageWrapper} via
 * {@link shouldExcludeSmsMessageWrapper} so automated SMS matches send.
 */
export function buildClinicSmsPreviewText({
    body,
    ctaText,
    ctaUrl,
    clinicName,
    excludeMessageWrapper,
    includeMarketingOptOutFooter,
}: BuildClinicSmsPreviewInput): string {
    const introText = excludeMessageWrapper
        ? ''
        : `Message from ${clinicName || 'Chckvet Appointments'}:\n`

    const ctaMessage = ctaText && ctaUrl ? `\n\n${ctaText}: ${ctaUrl}` : ''
    const marketingFooter = includeMarketingOptOutFooter
        ? `\n\n${BULK_MARKETING_SMS_OPT_OUT_POSTSCRIPT}`
        : ''

    return `${introText}${body}${ctaMessage}${marketingFooter}`
}

/**
 * Convenience wrapper: builds the clinic-rendered preview text and returns its
 * segment info. Applies the same GSM-7 sanitization the server uses before send
 * so the UI gate matches what will actually be billed / delivered.
 */
export function getClinicSmsSegmentInfo(
    input: BuildClinicSmsPreviewInput,
): SmsSegmentInfo {
    const preview = buildClinicSmsPreviewText({
        ...input,
        body: sanitizeForGsm7(input.body ?? ''),
        ctaText: input.ctaText ? sanitizeForGsm7(input.ctaText) : input.ctaText,
        ctaUrl: input.ctaUrl ? sanitizeForGsm7(input.ctaUrl) : input.ctaUrl,
    })
    const textForSegmentCount = input.assumeShortenedLinks
        ? replaceUrlsWithSmsShortLinkLength(preview)
        : preview
    return getSmsSegmentInfo(textForSegmentCount)
}

/**
 * Surfaces that author outbound SMS. Automated messages (notifications and
 * bulk) skip the "Message from {clinic}:" wrapper because those bodies already
 * include the clinic name. Inbox and auto-reply still honor the clinic setting.
 */
export type SmsAuthoringSurface =
    | 'inbox'
    | 'autoReply'
    | 'notification'
    | 'bulkMessage'

export function shouldExcludeSmsMessageWrapper(
    surface: SmsAuthoringSurface,
    clinicExcludesWrapper?: boolean | null,
): boolean {
    if (surface === 'notification' || surface === 'bulkMessage') {
        return true
    }

    return !!clinicExcludesWrapper
}

function isGsm7Character(character: string): boolean {
    return (
        GSM_7_BASIC_CHARACTERS.has(character) ||
        GSM_7_EXTENDED_CHARACTERS.has(character)
    )
}

function getSmsEncoding(text: string): SmsEncoding {
    return Array.from(text).every(isGsm7Character) ? 'GSM-7' : 'UCS-2'
}

function getGsm7UnitCount(text: string): number {
    return Array.from(text).reduce((total, character) => {
        if (GSM_7_EXTENDED_CHARACTERS.has(character)) {
            return total + 2
        }

        return total + 1
    }, 0)
}

function getNonGsm7CharacterName(char: string): string {
    if (NON_GSM7_CHARACTER_NAMES[char]) {
        return NON_GSM7_CHARACTER_NAMES[char]
    }

    // Emoji and other symbols — describe generically with a visible glyph when
    // the character itself is printable and not a control/format char.
    const code = char.codePointAt(0) ?? 0
    if (code >= 0x1f300 || (code >= 0x2600 && code <= 0x27bf)) {
        return `emoji (${char})`
    }

    // Control / format / combining marks are effectively invisible in the editor.
    if (
        code <= 0x1f ||
        (code >= 0x7f && code <= 0x9f) ||
        (code >= 0x200b && code <= 0x200f) ||
        (code >= 0x202a && code <= 0x202e) ||
        (code >= 0x2060 && code <= 0x206f) ||
        (code >= 0x300 && code <= 0x36f)
    ) {
        return 'invisible special character'
    }

    return `special character (${char})`
}

function buildExampleContext(chars: string[], index: number): string {
    const start = Math.max(0, index - 18)
    const end = Math.min(chars.length, index + 19)
    const slice = chars.slice(start, end).join('').replace(/\s+/g, ' ')
    const prefix = start > 0 ? '…' : ''
    const suffix = end < chars.length ? '…' : ''
    return `${prefix}${slice}${suffix}`
}
